import React, { useEffect, useMemo, useState } from "react";
import "./CreateInvoice.scss";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { adminRequest } from "../../../utils/requestMethods";
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const CreateInvoice = () => {
  const navigate = useNavigate();

  const initialItem = {
    title: "",
    amount: "",
    description: "",
  };

  const [formData, setFormData] = useState({
    course_id: "",
    student_id: "",
    semester: "",
    discount_amount: 0,
    discount_reason: "",
    include_previous_due: true,
    items: [initialItem],
  });

  const [programs, setPrograms] = useState([]);
  const [students, setStudents] = useState([]);

  const [studentSearch, setStudentSearch] = useState("");
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [previousDue, setPreviousDue] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];

    updatedItems[index][field] = value;

    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  const addItemRow = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          title: "",
          amount: "",
          description: "",
        },
      ],
    });
  };

  const removeItemRow = (index) => {
    const updatedItems = formData.items.filter(
      (_, itemIndex) => itemIndex !== index,
    );

    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  const totalAmount = useMemo(() => {
    return formData.items.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
  }, [formData.items]);

  const payableAmount = useMemo(() => {
    return (
      totalAmount +
      Number(formData.include_previous_due ? previousDue : 0) -
      Number(formData.discount_amount || 0)
    );
  }, [
    totalAmount,
    previousDue,
    formData.discount_amount,
    formData.include_previous_due,
  ]);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await adminRequest.get("/course/getNameAndId");

        setPrograms(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchPrograms();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!formData.course_id) {
        setStudents([]);
        setFilteredStudents([]);
        return;
      }

      try {
        const response = await adminRequest.post("/student/listByCourse", {
          id: formData.course_id,
        });

        setStudents(response.data.data);
        setFilteredStudents(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchStudents();
  }, [formData.course_id]);

  const handleStudentSearch = (value) => {
    setStudentSearch(value);

    setShowStudentDropdown(true);

    const filtered = students.filter((student) =>
      student.fullName.toLowerCase().includes(value.toLowerCase()),
    );

    setFilteredStudents(filtered);
  };

  const handleStudentSelect = async (student) => {
    setFormData({
      ...formData,
      student_id: student.id,
    });

    setStudentSearch(student.fullName);

    setShowStudentDropdown(false);

    try {
      const response = await adminRequest.get(
        `/invoice/previousDue/${student.id}`,
      );

      setPreviousDue(response.data.data.previous_due);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.student_id) {
      toast.error("Please select a student");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await toast.promise(
        adminRequest.post("/invoice/create", formData),
        {
          pending: "Creating invoice...",
        },
      );

      toast.success(response.data.message);

      setFormData({
        course_id: "",
        student_id: "",
        semester: "",
        discount_amount: 0,
        discount_reason: "",
        include_previous_due: true,
        items: [initialItem],
      });

      setStudentSearch("");
      setStudents([]);
      setFilteredStudents([]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="createInvoiceContainer">
      <div className="pageHeader">
        <div className="leftSection">
          <button
            type="button"
            className="backBtn"
            onClick={() => navigate(-1)}
          >
            <MdArrowBack />
          </button>

          <h2>Create Invoice</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="topFields">
          <div className="formGroup">
            <label>Program</label>

            <select
              name="course_id"
              value={formData.course_id}
              onChange={handleChange}
              required
            >
              <option value="">Select Course</option>

              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.short_name}
                </option>
              ))}
            </select>
          </div>

          <div className="formGroup studentSearchGroup">
            <label>Student Name</label>

            <input
              type="text"
              value={studentSearch}
              placeholder="Search student by name"
              onChange={(e) => handleStudentSearch(e.target.value)}
              onFocus={() => setShowStudentDropdown(true)}
              disabled={!formData.course_id}
              required
            />

            {showStudentDropdown && filteredStudents.length > 0 && (
              <div className="studentDropdown">
                {filteredStudents.map((student) => (
                  <div
                    key={student.id}
                    className="studentItem"
                    onClick={() => handleStudentSelect(student)}
                  >
                    <p>{student.fullName}</p>

                    <span>Id: {student.id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="formGroup">
            <label>Semester</label>

            <select
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              required
            >
              <option value="">Select Semester</option>
              <option value="First Semester">First Semester</option>
              <option value="Second Semester">Second Semester</option>
              <option value="Third Semester">Third Semester</option>
              <option value="Fourth Semester">Fourth Semester</option>
              <option value="Fifth Semester">Fifth Semester</option>
              <option value="Sixth Semester">Sixth Semester</option>
              <option value="Seventh Semester">Seventh Semester</option>
              <option value="Eighth Semester">Eighth Semester</option>
            </select>
          </div>
        </div>
        <div className="previousDueSection">
          <label className="checkboxWrapper">
            <input
              type="checkbox"
              checked={formData.include_previous_due}
              onChange={(e) =>
                setFormData({
                  ...formData,

                  include_previous_due: e.target.checked,
                })
              }
            />

            <span>Include Previous Due Payment</span>
          </label>
        </div>

        <div className="invoiceItemsSection">
          <div className="sectionHeader">
            <h3>Invoice Items</h3>

            <button type="button" className="addBtn" onClick={addItemRow}>
              + Add Item
            </button>
          </div>

          <div className="invoiceTable">
            {formData.items.map((item, index) => (
              <div className="invoiceItemRow" key={index}>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                  placeholder="Fee title"
                  required
                />

                <input
                  type="number"
                  value={item.amount}
                  onChange={(e) =>
                    handleItemChange(index, "amount", e.target.value)
                  }
                  placeholder="Amount"
                  required
                />

                <input
                  type="text"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(index, "description", e.target.value)
                  }
                  placeholder="Remarks"
                />

                <button
                  type="button"
                  className="removeBtn"
                  onClick={() => removeItemRow(index)}
                  disabled={formData.items.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="discountSection">
          <div className="formGroup">
            <label>Discount Amount</label>

            <input
              type="number"
              name="discount_amount"
              value={formData.discount_amount}
              onChange={handleChange}
              placeholder="Discount amount"
            />
          </div>

          <div className="formGroup">
            <label>Discount Reason</label>

            <input
              type="text"
              name="discount_reason"
              value={formData.discount_reason}
              onChange={handleChange}
              placeholder="Discount reason"
            />
          </div>
        </div>

        <div className="summarySection">
          <div className="summaryCard">
            <h4>Total Amount</h4>

            <p>Rs. {totalAmount}</p>
          </div>

          {formData.include_previous_due && previousDue > 0 && (
            <div className="summaryCard dueCard">
              <h4>Previous Due</h4>

              <p>Rs. {previousDue}</p>
            </div>
          )}

          <div className="summaryCard discountCard">
            <h4>Discount</h4>

            <p>Rs. {formData.discount_amount || 0}</p>
          </div>

          <div className="summaryCard payableCard">
            <h4>Payable Amount</h4>

            <p>Rs. {payableAmount}</p>
          </div>
        </div>

        <div className="submitSection">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Invoice"}
          </button>
        </div>
      </form>

      <ToastContainer position="top-center" />
    </div>
  );
};

export default CreateInvoice;
